import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  FormHelperText,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  useAvailableReplacementTeachers,
  useAssignReplacementTeacher,
  useReplacementCoverage,
  useClearReplacementTeacher,
  useClearReplacementWeeks,
} from '../../../hooks/useLessons';
import { AvailableTeacher, Lesson } from '../../../api/lessons.api';
import {
  currentWeekNumber,
  parseUTCDate,
  termWeekAnchor,
  occurrenceDateForWeek,
  formatInSchoolTz,
  termWeekCount,
  lessonWeekWindow,
  type LessonWeekWindow,
} from '../../../utils/dateUtils';
import { addDays } from 'date-fns';
import WeekTilePicker from '../../booking/WeekTilePicker';
import { useSessionWeeks, clinicOnlyWeeksFor } from './sessionWeeks';
import { useSchoolTimezone } from '../../../contexts/TenantContext';

interface AssignReplacementTeacherDialogProps {
  open: boolean;
  lesson: Lesson;
  onClose: () => void;
  onSubmit?: () => void;
}

const MIN_REASON_LENGTH = 5;
const MAX_REASON_LENGTH = 500;

/**
 * Compute the non-past term weeks for a lesson, plus the upcoming week (the
 * sensible default for a fresh assign). "Past" is judged by the actual LESSON
 * OCCURRENCE date (termStart + (N-1)*7 shifted to the lesson's day-of-week) —
 * NOT the week-end — so it matches both the WeekTilePicker's disabled state
 * and the backend's per-week rule. Using the week-end here would wrongly keep
 * the current week "remaining" after its lesson day has passed, leaving a
 * disabled tile stuck in the pre-seeded selection.
 */
function useTermWeeks(
  termStartDate?: string,
  termEndDate?: string,
  dayOfWeek?: number | null,
  sessionWeeks?: number[] | null,
  // The CLASS window (mid-term start / last week). Weeks outside it are neither
  // 'remaining' nor 'past' targets: the class did not exist then. Omitted = whole term.
  classWindow?: LessonWeekWindow | null
) {
  return useMemo(() => {
    if (!termStartDate || !termEndDate) {
      return {
        allWeeks: [] as number[],
        remainingWeeks: [] as number[],
        pastWeeks: [] as number[],
        nextWeek: 0,
      };
    }
    const sessionSet = sessionWeeks ? new Set(sessionWeeks) : null;
    const lastWeek = currentWeekNumber(termStartDate, parseUTCDate(termEndDate));
    const termStart = parseUTCDate(termStartDate);
    const termEnd = parseUTCDate(termEndDate);
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

    const allWeeks: number[] = [];
    const remainingWeeks: number[] = [];
    // Weeks whose occurrence has passed but that DO have a real session inside
    // the term — the ones a retroactive correction can legitimately target.
    // Deliberately separate from `remainingWeeks`: "Select all remaining weeks"
    // and the seed default must never sweep history into a bulk action.
    const pastWeeks: number[] = [];
    for (let week = 1; week <= lastWeek; week++) {
      if (classWindow && (week < classWindow.first || week > classWindow.last)) continue;
      allWeeks.push(week);
      // Weeks the lesson doesn't run (hybrid/band) are never "remaining".
      if (sessionSet && !sessionSet.has(week)) continue;
      let isPast: boolean;
      let hasOccurrenceInTerm = true;
      if (dayOfWeek != null) {
        // Occurrence date for this week (matches WeekTilePicker + backend);
        // Monday-anchored weeks, bounds-checked against the term dates.
        const occurrence = occurrenceDateForWeek(termStart, week, dayOfWeek);
        // Out-of-term is NOT "past" — a Monday-anchored week 1 can start before
        // the term begins, and there is no occurrence there to attribute. The
        // backend rejects those separately ("no lesson occurrence within the
        // term"), so they must not be offered as retroactive targets either.
        hasOccurrenceInTerm = occurrence >= termStart && occurrence <= termEnd;
        isPast = occurrence < today || !hasOccurrenceInTerm;
        if (hasOccurrenceInTerm && occurrence < today) pastWeeks.push(week);
      } else {
        // Fallback (no lesson day): use the week-end boundary.
        const rawEnd = addDays(termWeekAnchor(termStart), (week - 1) * 7 + 6);
        const end = rawEnd > termEnd ? termEnd : rawEnd;
        isPast = end < today;
        if (isPast) pastWeeks.push(week);
      }
      if (!isPast) remainingWeeks.push(week);
    }
    return { allWeeks, remainingWeeks, pastWeeks, nextWeek: remainingWeeks[0] ?? 0 };
  }, [termStartDate, termEndDate, dayOfWeek, sessionWeeks, classWindow]);
}

export function AssignReplacementTeacherDialog({
  open,
  lesson,
  onClose,
  onSubmit,
}: AssignReplacementTeacherDialogProps) {
  const lessonId = lesson.id;
  const termStartDate = lesson.Term?.startDate;
  const termEndDate = lesson.Term?.endDate;

  const [selectedTeacherId, setSelectedTeacherId] = useState<string>('');
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [reason, setReason] = useState<string>('');
  const [reasonError, setReasonError] = useState<string>('');
  // Weeks that already had a per-week replacement when the dialog opened — used
  // on submit to clear the ones the admin unticks (assign is additive backend-side).
  const [originalWeeks, setOriginalWeeks] = useState<number[]>([]);
  // Guard so the seed runs exactly once per open (prevents a later coverage
  // refetch from clobbering the admin's in-progress edits).
  const seededRef = useRef(false);

  // Admin action timestamps are real instants, so they render in the SCHOOL's
  // timezone (falls back to Australia/Sydney before the tenant loads).
  const schoolTimeZone = useSchoolTimezone();
  const allSessionWeeks = useSessionWeeks(lesson);
  // 1:1 clinic weeks are NOT covered by a per-week replacement — the backend
  // refuses them (400) and clinic teachers change per booking. Lock the tiles
  // and say so, rather than let the admin believe the week is covered.
  const clinicWeeks = useMemo(() => clinicOnlyWeeksFor(lesson), [lesson]);
  const sessionWeeks = useMemo(() => {
    if (!allSessionWeeks || clinicWeeks.length === 0) return allSessionWeeks;
    const locked = new Set(clinicWeeks);
    return allSessionWeeks.filter((w) => !locked.has(w));
  }, [allSessionWeeks, clinicWeeks]);
  // `nextWeek` is intentionally NOT destructured — nothing pre-selects a week
  // any more (see the seed effect below). It stays on the hook's return for any
  // future caller that wants a sensible default.
  const classWindow = useMemo(
    () =>
      termStartDate && termEndDate
        ? lessonWeekWindow(lesson, termWeekCount(termStartDate, termEndDate))
        : null,
    [lesson, termStartDate, termEndDate]
  );
  const { remainingWeeks, pastWeeks } = useTermWeeks(
    termStartDate,
    termEndDate,
    lesson.dayOfWeek,
    sessionWeeks,
    classWindow
  );

  // Which of the admin's picks are retroactive. Drives the warning banner and
  // the `allowPast` flag — sent ONLY when history is actually being edited, so a
  // normal forward assignment is byte-identical to before.
  const selectedPastWeeks = useMemo(
    () => selectedWeeks.filter((w) => pastWeeks.includes(w)).sort((a, b) => a - b),
    [selectedWeeks, pastWeeks]
  );
  const isRetroactive = selectedPastWeeks.length > 0;
  // "Future" means explicitly in `remainingWeeks`, NOT merely "absent from
  // pastWeeks". A week the lesson doesn't run lands in NEITHER list (the loop
  // skips it), so `!pastWeeks.includes(w)` would call it future. Unreachable
  // today — the picker hard-disables no-session weeks and the backend refuses to
  // write one — but stating the positive condition means this can't quietly
  // become wrong if either guard is ever relaxed.
  const hasFutureSelection = selectedWeeks.some((w) => remainingWeeks.includes(w));

  const inClassWindow = useCallback(
    (week: number) => !classWindow || (week >= classWindow.first && week <= classWindow.last),
    [classWindow]
  );

  const { data: coverage, isLoading: isLoadingCoverage } = useReplacementCoverage(
    open ? lessonId : undefined
  );
  const isEditing = !!coverage && (coverage.weeks.length > 0 || !!coverage.global);

  // Teacher list reacts to the selected weeks (free on ALL of them).
  const { data: availableTeachers = [], isLoading: isLoadingTeachers } =
    useAvailableReplacementTeachers(open ? lessonId : undefined, selectedWeeks);

  const { mutate: assignTeacher, isPending: isAssigning } = useAssignReplacementTeacher();
  const { mutate: clearAll, isPending: isClearingAll } = useClearReplacementTeacher();
  const { mutate: clearWeeks, isPending: isClearingWeeks } = useClearReplacementWeeks();

  const busy = isAssigning || isClearingAll || isClearingWeeks;

  // Seed the dialog ONCE per open, after coverage resolves (handles both cold-
  // and warm-cache paths because `coverage` is a dep + the ref guards re-seed):
  //  - existing per-week coverage   → tick those weeks; pre-fill teacher + reason
  //  - existing whole-term (global) → tick all remaining weeks (locked decision #5); pre-fill
  //  - fresh assign                 → tick just the next upcoming week
  // The seededRef prevents a later coverage refetch (e.g. from invalidation)
  // from clobbering the admin's in-progress edits.
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (isLoadingCoverage || seededRef.current) return;

    // A window SHRINK keeps its LessonException rows, so coverage can name weeks the
    // class no longer runs. They are not this dialog's business: seeding one would
    // render it ticked AND disabled (unremovable) and every save would 400 on it.
    const coveredWeeks = (coverage?.weeks ?? [])
      .filter((w) => inClassWindow(w.weekNumber))
      .sort((a, b) => a.weekNumber - b.weekNumber);
    if (coverage && coveredWeeks.length > 0) {
      const weeks = coveredWeeks.map((w) => w.weekNumber);
      setSelectedWeeks(weeks);
      setOriginalWeeks(weeks);
      setSelectedTeacherId(coveredWeeks[0].replacementTeacher.id);
      setReason(coveredWeeks[0].reason ?? '');
    } else if (coverage && coverage.global) {
      setSelectedWeeks(remainingWeeks);
      setOriginalWeeks([]); // global isn't per-week, nothing to diff-clear
      setSelectedTeacherId(coverage.global.replacementTeacher.id);
      setReason(coverage.global.reason ?? '');
    } else {
      // Fresh assign: select NOTHING. The admin picks the week.
      //
      // This used to pre-tick `nextWeek`, which actively fought the retroactive
      // case: ticking a past week left the pre-ticked FUTURE week selected too,
      // making the selection "mixed" — and a mixed selection keeps the strict
      // availability filter, so the teacher dropdown silently emptied while the
      // past-week notice was showing. The admin had to notice and untick a week
      // they never chose. Guessing the week was never worth that.
      setSelectedWeeks([]);
      setOriginalWeeks([]);
    }
    seededRef.current = true;
  }, [open, isLoadingCoverage, coverage, remainingWeeks, inClassWindow]);

  // Drop a selected teacher who is no longer free on all selected weeks (the
  // available list refetches as weeks change) so we never submit an invalid one.
  useEffect(() => {
    if (isLoadingTeachers || !selectedTeacherId) return;
    if (!availableTeachers.some((t) => t.id === selectedTeacherId)) {
      setSelectedTeacherId('');
    }
  }, [availableTeachers, isLoadingTeachers, selectedTeacherId]);

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setReason(value);
    if (value && value.length < MIN_REASON_LENGTH) {
      setReasonError(`Reason must be at least ${MIN_REASON_LENGTH} characters`);
    } else if (value.length > MAX_REASON_LENGTH) {
      setReasonError(`Reason must not exceed ${MAX_REASON_LENGTH} characters`);
    } else {
      setReasonError('');
    }
  };

  const toggleWeek = (week: number) => {
    setSelectedWeeks((prev) =>
      prev.includes(week) ? prev.filter((w) => w !== week) : [...prev, week].sort((a, b) => a - b)
    );
  };

  // ⚠️ Deliberately `remainingWeeks` (future only), NOT all weeks. Past weeks are
  // individually selectable for retroactive corrections, but a BULK control must
  // never sweep history into an edit the admin didn't consider — and "remaining"
  // has meant non-past since the feature's locked decisions #5/#7.
  const selectAllRemaining = () => setSelectedWeeks(remainingWeeks);

  const resetState = () => {
    setSelectedTeacherId('');
    setSelectedWeeks([]);
    setReason('');
    setReasonError('');
    setOriginalWeeks([]);
  };

  const handleClose = () => {
    if (busy) return;
    resetState();
    onClose();
  };

  const finishAndClose = () => {
    resetState();
    onClose();
    onSubmit?.();
  };

  const handleSubmit = () => {
    // Defence in depth: never submit a week outside the class window (the server 400s it).
    const weekNumbers = selectedWeeks.filter(inClassWindow);
    if (!selectedTeacherId || weekNumbers.length === 0 || !reason) return;

    // Assign is additive backend-side, so weeks the admin unticked that were
    // previously covered must be explicitly reinstated to the regular teacher.
    const removedWeeks = originalWeeks.filter((w) => !weekNumbers.includes(w));

    assignTeacher(
      {
        lessonId,
        data: {
          replacementTeacherId: selectedTeacherId,
          replacementReason: reason,
          weekNumbers,
          // Only opt in when history is actually being edited. Sending it
          // unconditionally would quietly disable the server's past-week guard
          // for every assignment this dialog makes.
          ...(isRetroactive ? { allowPast: true } : {}),
        },
      },
      {
        onSuccess: () => {
          if (removedWeeks.length > 0) {
            clearWeeks(
              { lessonId, weekNumbers: removedWeeks },
              { onSuccess: finishAndClose, onError: finishAndClose }
            );
          } else {
            finishAndClose();
          }
        },
      }
    );
  };

  const handleReinstateAll = () => {
    clearAll(lessonId, {
      onSuccess: () => {
        resetState();
        onClose();
        onSubmit?.();
      },
    });
  };

  const currentCoverageLabel = useMemo(() => {
    if (!coverage) return '';
    if (coverage.weeks.length > 0) {
      const t = coverage.weeks[0].replacementTeacher;
      const weeks = coverage.weeks.map((w) => w.weekNumber).sort((a, b) => a - b);
      const names = Array.from(
        new Set(coverage.weeks.map((w) => `${w.replacementTeacher.firstName} ${w.replacementTeacher.lastName}`))
      );
      const who = names.length === 1 ? `${t.firstName} ${t.lastName}` : names.join(', ');
      return `${who} — Week${weeks.length > 1 ? 's' : ''} ${weeks.join(', ')}`;
    }
    if (coverage.global) {
      const t = coverage.global.replacementTeacher;
      return `${t.firstName} ${t.lastName} — all remaining weeks`;
    }
    return '';
  }, [coverage]);

  const isFormValid =
    !!selectedTeacherId &&
    selectedWeeks.length > 0 &&
    reason.length >= MIN_REASON_LENGTH &&
    reason.length <= MAX_REASON_LENGTH &&
    !reasonError;

  // What is still outstanding, in the order the form is filled in. A disabled
  // submit button with no stated cause is the single most confusing thing this
  // dialog can do — the admin has to guess which of three conditions is unmet
  // (and the reason minimum in particular is easy to miss).
  const missingRequirements = useMemo(() => {
    const missing: string[] = [];
    // The week picker renders its own "Select at least one week" hint directly
    // beneath the tiles, so repeating it here would show the same message twice.
    if (selectedWeeks.length === 0) missing.push('select a week above');
    if (!selectedTeacherId) missing.push('choose a replacement teacher');
    if (reason.length < MIN_REASON_LENGTH) {
      missing.push(`add a reason (at least ${MIN_REASON_LENGTH} characters)`);
    }
    return missing;
  }, [selectedWeeks.length, selectedTeacherId, reason.length]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Replacement Teacher' : 'Assign Replacement Teacher'}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Current coverage summary (edit mode) */}
          {isEditing && currentCoverageLabel && (
            <Alert severity="info">
              <Typography variant="body2">
                Currently: <strong>{currentCoverageLabel}</strong>
              </Typography>
              {/* Actor trail — who entered each record and when. Matters most
                  for retroactive corrections, which is why it is shown here
                  rather than hidden behind a details view. `recordedBy` is null
                  when the admin has since been deleted, so render defensively. */}
              {coverage?.weeks
                .filter((w) => w.recordedBy || w.recordedAt)
                .map((w) => (
                  <Typography
                    key={w.weekNumber}
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Week {w.weekNumber} recorded
                    {w.recordedBy
                      ? ` by ${w.recordedBy.firstName} ${w.recordedBy.lastName}`
                      : ''}
                    {/* `recordedAt` is a real INSTANT (createdAt), so it must be
                        rendered in the SCHOOL's timezone — not UTC, and not the
                        viewer's browser. Using formatUTCDate here showed an
                        Australian admin YESTERDAY's date for any action taken
                        before ~10am (22:30Z on the 18th is 08:30 on the 19th in
                        Sydney). That is the documented split in dateUtils:
                        instants → formatInSchoolTz, @db.Date → formatUTCDate.
                        Note `w.originalDate` alongside is a @db.Date and would
                        need the opposite helper. */}
                    {w.recordedAt
                      ? ` · ${formatInSchoolTz(w.recordedAt, schoolTimeZone, {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}`
                      : ''}
                  </Typography>
                ))}
            </Alert>
          )}

          {/* Week picker */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Weeks to replace</Typography>
              {remainingWeeks.length > 0 && (
                <Button
                  variant="text"
                  size="small"
                  onClick={selectAllRemaining}
                  disabled={busy}
                >
                  Select all remaining weeks
                </Button>
              )}
            </Box>
            {termStartDate && termEndDate ? (
              <WeekTilePicker
                termStartDate={termStartDate}
                termEndDate={termEndDate}
                multiSelect
                selectedWeeks={selectedWeeks}
                onToggleWeek={toggleWeek}
                occurrenceDayOfWeek={lesson.dayOfWeek ?? undefined}
                sessionWeeks={sessionWeeks ?? undefined}
                windowFirst={classWindow?.first}
                windowLast={classWindow?.last}
                // "Post-edit classes": an admin correcting the record after the
                // fact (MNM: "in WK 2 and 3 they had a different teacher").
                // This is a SOURCE picker — the week identifies an existing
                // occurrence — so the default `pastRule='occurrence'` is
                // correct and only the past-date lock is relaxed. Weeks with no
                // session, and weeks whose occurrence falls outside the term,
                // stay locked regardless.
                allowPast
              />
            ) : (
              <FormHelperText>This lesson has no term schedule.</FormHelperText>
            )}
            {clinicWeeks.length > 0 && (
              <FormHelperText>
                1:1 clinic {clinicWeeks.length > 1 ? 'weeks' : 'week'} {clinicWeeks.map((w) => `Wk ${w}`).join(', ')}{' '}
                {clinicWeeks.length > 1 ? 'are' : 'is'} not covered here — change the teacher on each clinic booking instead.
              </FormHelperText>
            )}
            {selectedWeeks.length === 0 && (
              <FormHelperText error>Select at least one week</FormHelperText>
            )}
          </Box>

          {/* Retroactive edit notice. Two things the admin must know before
              saving: this is a records correction, and NOBODY will be emailed
              about it. Silence is otherwise indistinguishable from a failure. */}
          {isRetroactive && (
            <Alert severity="warning" icon={<HistoryIcon />}>
              <Typography variant="subtitle2" gutterBottom>
                {selectedPastWeeks.length > 1
                  ? `Weeks ${selectedPastWeeks.join(', ')} have already passed`
                  : `Week ${selectedPastWeeks[0]} has already passed`}
              </Typography>
              <Typography variant="body2">
                This records who actually taught{' '}
                {selectedPastWeeks.length > 1 ? 'those classes' : 'that class'} — useful when cover
                was arranged at short notice and never entered at the time.{' '}
                <strong>
                  No email will be sent for{' '}
                  {selectedPastWeeks.length > 1 ? 'these past weeks' : 'this past week'}.
                </strong>
                {hasFutureSelection &&
                  ' Parents and the replacement teacher will still be notified about the upcoming weeks you selected.'}
              </Typography>
            </Alert>
          )}

          <Divider />

          {/* Teacher Select — while availability is (re)computed for the
              selected weeks, the dropdown stays empty + disabled and a spinner
              with a "checking…" note shows in the helper text below (kept OUT
              of the value area so it never overlaps the floating label). */}
          <FormControl fullWidth disabled={isLoadingTeachers || selectedWeeks.length === 0}>
            <InputLabel>Select Teacher</InputLabel>
            <Select
              value={isLoadingTeachers ? '' : selectedTeacherId}
              onChange={(e) => setSelectedTeacherId(e.target.value)}
              label="Select Teacher"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableTeachers.map((teacher: AvailableTeacher) => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  {teacher.firstName} {teacher.lastName}
                  {teacher.instruments.length > 0 &&
                    ` - ${teacher.instruments.map((i) => i.name).join(', ')}`}
                </MenuItem>
              ))}
            </Select>
            {selectedWeeks.length === 0 ? (
              <FormHelperText>Choose weeks first to see who's available</FormHelperText>
            ) : isLoadingTeachers ? (
              <FormHelperText component="div">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} />
                  <span>Checking which teachers are free on all selected weeks…</span>
                </Box>
              </FormHelperText>
            ) : availableTeachers.length === 0 ? (
              <FormHelperText>No teachers free on all selected weeks</FormHelperText>
            ) : null}
          </FormControl>

          {/* Reason TextField */}
          <TextField
            fullWidth
            required
            multiline
            rows={4}
            label="Replacement Reason"
            placeholder="Explain why this teacher is being assigned (e.g., original teacher illness, professional development, etc.)"
            value={reason}
            onChange={handleReasonChange}
            error={!!reasonError}
            // An EMPTY field previously showed only a character counter, which
            // reads as optional — so the reason minimum was the least obvious of
            // the three things blocking submit. `reasonError` already covers the
            // too-short and too-long cases once typing starts.
            helperText={
              reasonError ||
              (reason.length === 0
                ? `Required — at least ${MIN_REASON_LENGTH} characters`
                : `${reason.length}/${MAX_REASON_LENGTH} characters`)
            }
            disabled={busy}
          />

          {/* Say plainly what is still outstanding. Without this the only signal
              is a greyed-out button, and the admin has to guess which of three
              conditions is unmet. Not rendered once the form is complete. */}
          {missingRequirements.length > 0 && (
            <FormHelperText sx={{ mt: -1 }}>
              To continue, {missingRequirements.join(', ')}.
            </FormHelperText>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {/* Reinstate-all (edit mode only) on the left */}
        <Box>
          {isEditing && (
            <Button
              color="error"
              onClick={handleReinstateAll}
              disabled={busy}
              endIcon={isClearingAll ? <CircularProgress size={18} /> : undefined}
            >
              Reinstate regular teacher (all weeks)
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid || busy}
            endIcon={isAssigning ? <CircularProgress size={20} /> : undefined}
          >
            {isAssigning ? 'Saving...' : isEditing ? 'Save Changes' : 'Assign Teacher'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
